import React, { useMemo } from 'react';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { JiraIcon } from '@atlaskit/logo';
import { defaultLocale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { getAppliedIntervalDecoration } from '@atlassian/jira-polaris-component-decorations/src/utils/index.tsx';
import type { ValueDecoration } from '@atlassian/jira-polaris-domain-field/src/decoration/types.tsx';
import { renderDateString } from '@atlassian/jira-polaris-lib-date-time/src/index.tsx';
import { CommonDecoratedTag } from '@atlassian/jira-polaris-lib-decoration/src/ui/decoration/common/index.tsx';
import { useEmoji } from '@atlassian/jira-polaris-lib-emoji-picker/src/controllers/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { INTERVAL_FIELD_SOURCES } from '@atlassian/jira-polaris-domain-field/src/field/interval/index.tsx';
import { DELIVERY_DATE_AGGREGATION_TYPES } from '@atlassian/jira-polaris-domain-field/src/delivery/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { tryParseIntervalString } from '@atlassian/jira-polaris-lib-date-time/src/utils.tsx';
import type { CommonFieldComponentProps } from '../../common/types.tsx';

import {
	useFieldValueDecorations,
	useFieldConfiguration,
} from '../../controllers/fields/index.tsx';
import messages from './messages.tsx';

const AGGREGATION_TYPE_TO_TOOLTIP_MESSAGE = {
	[DELIVERY_DATE_AGGREGATION_TYPES.LATEST]: messages.tooltipDeliveryDateOverrideLatest,
	[DELIVERY_DATE_AGGREGATION_TYPES.EARLIEST]: messages.tooltipDeliveryDateOverrideEarliest,
};

export const isIntervalFieldComponentProps = (
	props: CommonFieldComponentProps,
): props is IntervalFieldComponentProps =>
	('value' in props && typeof props.value === 'string') ||
	('calculatedValue' in props && typeof props.calculatedValue === 'string');

export type IntervalFieldComponentProps = CommonFieldComponentProps & {
	value: string;
	calculatedValue?: string;
	isInsideTooltip?: boolean;
};

export const IntervalField = ({
	fieldKey,
	value,
	calculatedValue,
	isInsideTooltip,
}: IntervalFieldComponentProps) => {
	const locale = useLocale();
	const { formatMessage } = useIntl();
	const [fieldValueDecorations] = useFieldValueDecorations(fieldKey);
	const [fieldConfiguration] = useFieldConfiguration(fieldKey);

	const displayValue = calculatedValue || value;

	const parsedValue = useMemo(() => {
		return fg('jpd-safe-interval-parse')
			? tryParseIntervalString(displayValue)
			: JSON.parse(displayValue);
	}, [displayValue]);

	const intervalFormatted = useMemo(() => {
		return renderDateString(parsedValue);
	}, [parsedValue]);

	if (!intervalFormatted) {
		return null;
	}

	const decoration = getAppliedIntervalDecoration(
		fieldValueDecorations,
		parsedValue,
		locale || defaultLocale,
	);

	if (calculatedValue) {
		if (
			fieldConfiguration?.source === INTERVAL_FIELD_SOURCES.DELIVERY_DATE &&
			fieldConfiguration?.aggregationType
		) {
			const tooltipMessage =
				AGGREGATION_TYPE_TO_TOOLTIP_MESSAGE[fieldConfiguration.aggregationType];
			// Interval type does not have it's own icon tied to it, so we use the icon for delivery status

			if (isInsideTooltip) {
				return (
					<Inline xcss={inlineStyles}>
						<IntervalFieldDisplay value={intervalFormatted} decoration={decoration} />
						<JiraIcon size="xsmall" label={formatMessage(tooltipMessage)} />
					</Inline>
				);
			}

			return (
				<Tooltip content={formatMessage(tooltipMessage)}>
					<Inline xcss={inlineStyles}>
						<IntervalFieldDisplay value={intervalFormatted} decoration={decoration} />
						<JiraIcon size="xsmall" label={formatMessage(tooltipMessage)} />
					</Inline>
				</Tooltip>
			);
		}
	}

	return <IntervalFieldDisplay value={intervalFormatted} decoration={decoration} />;
};

export const IntervalFieldDisplay = ({
	value,
	decoration,
}: {
	value: string;
	calculatedValue?: string;
	decoration: ValueDecoration | undefined;
}) => {
	const emojiDescription = useEmoji(decoration?.emoji);

	return (
		<Box xcss={boxStyles}>
			<CommonDecoratedTag
				value={value}
				mainColor={decoration?.backgroundColor}
				emoji={emojiDescription}
			/>
		</Box>
	);
};

const boxStyles = xcss({
	width: 'max-content',
});

const inlineStyles = xcss({
	alignItems: 'center',
});
